import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  GetApp as InstallIcon,
  Smartphone as SmartphoneIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import QRCode from 'react-qr-code';

const PWATab: React.FC = () => {
  // PWA Installation states
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  
  // Get current URL for QR code
  const currentUrl = window.location.origin;

  useEffect(() => {
    // PWA Installation logic
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
      
      // Show proactive install prompt after a short delay
      setTimeout(() => {
        if (!isInstalled) {
          toast.info('📱 Install our app for a better experience!', {
            position: 'bottom-right',
            autoClose: 8000,
            onClick: () => handleInstallApp()
          });
        }
      }, 3000);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      toast.success('App installed successfully!');
    };

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        (window.navigator as any).standalone === true) {
      setIsInstalled(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        toast.success('App installation started!');
      } else {
        toast.info('App installation cancelled');
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    } else {
      toast.info('App installation not available on this device/browser');
    }
  };

  const handleShareApp = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Stock Strategy App',
          text: 'Check out this amazing stock analysis app!',
          url: currentUrl,
        });
        toast.success('App shared successfully!');
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(currentUrl);
        toast.success('App URL copied to clipboard!');
      } catch (error) {
        toast.error('Failed to copy URL');
      }
    }
  };

  const getInstallationInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      return {
        browser: 'Chrome',
        steps: [
          'Click the install button above or the install icon in the address bar',
          'Click "Install" in the popup dialog',
          'The app will be added to your home screen/desktop'
        ]
      };
    } else if (userAgent.includes('firefox')) {
      return {
        browser: 'Firefox',
        steps: [
          'Click the three dots menu in the address bar',
          'Select "Install [App Name]"',
          'Click "Add" to install the app'
        ]
      };
    } else if (userAgent.includes('safari')) {
      return {
        browser: 'Safari (iOS)',
        steps: [
          'Tap the Share button at the bottom of the screen',
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" to confirm'
        ]
      };
    } else if (userAgent.includes('edg')) {
      return {
        browser: 'Microsoft Edge',
        steps: [
          'Click the install button above or the app icon in the address bar',
          'Click "Install" in the dialog',
          'The app will be added to your Start menu and desktop'
        ]
      };
    } else {
      return {
        browser: 'Your Browser',
        steps: [
          'Look for an install icon in your browser\'s address bar',
          'Click it and follow the prompts to install',
          'Alternatively, use the QR code to open on a mobile device'
        ]
      };
    }
  };

  const instructions = getInstallationInstructions();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Progressive Web App (PWA)
      </Typography>

      <Grid container spacing={3}>
        {/* Installation Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" component="div">
                  <SmartphoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Installation Status
                </Typography>
                <Chip 
                  label={isInstalled ? 'Installed' : 'Not Installed'} 
                  color={isInstalled ? 'success' : 'warning'}
                />
              </Box>
              
              {isInstalled ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Great! The app is installed and running as a PWA.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Install the app for a better experience with offline access and native app feel.
                </Alert>
              )}

              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<InstallIcon />}
                  onClick={handleInstallApp}
                  disabled={isInstalled || !showInstallPrompt}
                >
                  {isInstalled ? 'Already Installed' : 'Install App'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShareApp}
                >
                  Share App
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* QR Code */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <QrCodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                QR Code Access
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Scan this QR code with your mobile device to quickly access the app
              </Typography>

              <Box display="flex" justifyContent="center" mb={2}>
                <Box 
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'white', 
                    borderRadius: 1,
                    display: 'inline-block'
                  }}
                >
                  <QRCode
                    size={150}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={currentUrl}
                    viewBox={`0 0 256 256`}
                  />
                </Box>
              </Box>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => setQrCodeDialogOpen(true)}
              >
                View Larger QR Code
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Installation Instructions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Installation Instructions for {instructions.browser}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Follow these steps to install the app on your device:
            </Typography>

            <Box component="ol" sx={{ pl: 2 }}>
              {instructions.steps.map((step, index) => (
                <Box component="li" key={index} sx={{ mb: 1 }}>
                  <Typography variant="body2">{step}</Typography>
                </Box>
              ))}
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Benefits of installing:</strong> Faster loading, offline access, 
                native app experience, and home screen/desktop shortcut.
              </Typography>
            </Alert>
          </Paper>
        </Grid>

        {/* App Features */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              PWA Features
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h6" color="primary">📱</Typography>
                  <Typography variant="subtitle2" gutterBottom>Native Feel</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Looks and feels like a native mobile app
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h6" color="primary">⚡</Typography>
                  <Typography variant="subtitle2" gutterBottom>Fast Loading</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Instant loading with cached resources
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h6" color="primary">🔔</Typography>
                  <Typography variant="subtitle2" gutterBottom>Push Notifications</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Receive important updates and alerts
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h6" color="primary">🏠</Typography>
                  <Typography variant="subtitle2" gutterBottom>Home Screen</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Quick access from home screen or desktop
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Large QR Code Dialog */}
      <Dialog 
        open={qrCodeDialogOpen} 
        onClose={() => setQrCodeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <QrCodeIcon />
            Scan QR Code
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Scan this QR code with your mobile device to access the Stock Strategy App
            </Typography>
            
            <Box 
              sx={{ 
                p: 3, 
                backgroundColor: 'white', 
                borderRadius: 2,
                display: 'inline-block'
              }}
            >
              <QRCode
                size={256}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                value={currentUrl}
                viewBox={`0 0 256 256`}
              />
            </Box>
            
            <Typography variant="caption" color="text.secondary" textAlign="center">
              URL: {currentUrl}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrCodeDialogOpen(false)}>Close</Button>
          <Button onClick={handleShareApp} variant="contained">
            Share
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PWATab;


